// ============================================
// Chat Module - Message Actions (ES6)
// AI 메시지 액션 버튼 및 선택지 관리
// ============================================

import { CHAT_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import {
  getCurrentRoomId,
  getCurrentSessionId,
  getCurrentTurnCount,
  setCurrentTurnCount,
  isProcessing
} from '../app/state.js';
import { formatAIMessage } from './parser.js';
import { sendMessage } from './send.js';
import { showToast, escapeHtml, showGlobalLoading, hideGlobalLoading } from '../utils.js';
import { CURRENCY } from '../constants.js';
import { showConfirm } from '../shared/confirm-modal.js';
import { showImageModal } from '../image.js';
import { invalidateImagesCache } from '../memory.js';

// ============================================
// 편집 모달 상태
// ============================================
let editingMessageDiv = null;

// ============================================
// 선택지 관련 상태
// ============================================
let currentChoices = [];
let isChoiceEnabled = localStorage.getItem('choiceEnabled') !== 'false';

// ============================================
// 스토리 진행 함수
// ============================================

// 다음 이야기 생성 (유저 입력 없이 스토리 진행)
export async function continueStory() {
  if (isProcessing()) {
    showToast('응답 생성 중입니다. 잠시 후 시도해주세요.');
    return;
  }
  // "[계속]" 특수 입력으로 sendMessage 호출
  await sendMessage('[계속]');
}

// 마지막 AI 응답 재생성
export async function regenerateLastResponse() {
  if (isProcessing()) {
    showToast('응답 생성 중입니다. 잠시 후 시도해주세요.');
    return;
  }

  // 마지막 AI 메시지 찾기
  const aiMessages = document.querySelectorAll('.message.assistant');
  if (aiMessages.length === 0) {
    showToast('재생성할 AI 응답이 없습니다.');
    return;
  }

  const lastAiMsg = aiMessages[aiMessages.length - 1];
  const targetTurn = parseInt(lastAiMsg.dataset.turn) || getCurrentTurnCount();

  // 마지막 유저 메시지 찾기 (있을 경우)
  const userMessages = document.querySelectorAll('.message.user');
  let lastUserMsg = null;
  let lastUserContent = '';
  let isContinueMode = false;

  if (userMessages.length > 0) {
    lastUserMsg = userMessages[userMessages.length - 1];
    lastUserContent = lastUserMsg.querySelector('.message-content')?.textContent.trim() || '';
    const lastUserTurn = parseInt(lastUserMsg.dataset.turn) || 0;

    // "[계속]" 메시지이거나, AI turn > User turn이면 다음 이야기 모드
    if (lastUserContent === '[계속]' || targetTurn > lastUserTurn) {
      isContinueMode = true;
    }
  } else {
    // 유저 메시지가 없으면 다음 이야기 모드 (예: 첫 AI 응답 재생성)
    isContinueMode = true;
  }

  try {
    showGlobalLoading('재생성 중...');

    // 롤백 API 호출
    const rollbackResponse = await authFetch(CHAT_ENDPOINTS.rollbackMessages, {
      body: {
        roomId: getCurrentRoomId(),
        sessionId: getCurrentSessionId(),
        targetTurn: isContinueMode ? targetTurn - 1 : targetTurn - 1
      }
    });

    const rollbackResult = await rollbackResponse.json();
    if (!rollbackResult.success) {
      throw new Error(rollbackResult.error || '롤백 실패');
    }

    // UI에서 메시지 제거
    lastAiMsg.remove();
    if (lastUserMsg && !isContinueMode) {
      lastUserMsg.remove();
    } else if (lastUserMsg && isContinueMode && lastUserContent === '[계속]') {
      // "[계속]" 유저 메시지도 제거
      lastUserMsg.remove();
    }

    // 턴 카운트 감소
    setCurrentTurnCount(targetTurn - 1);

    hideGlobalLoading();

    // 재생성
    if (isContinueMode) {
      await sendMessage('[계속]');
    } else {
      await sendMessage(lastUserContent);
    }

  } catch (error) {
    hideGlobalLoading();
    console.error('Regenerate error:', error);
    showToast('재생성 중 오류가 발생했습니다.', true);
  }
}

// ============================================
// 메시지 편집 모달
// ============================================

export function openEditMessageModal(btnElement) {
  const messageDiv = btnElement.closest('.message.assistant');
  if (!messageDiv) {
    showToast('메시지를 찾을 수 없습니다.');
    return;
  }

  editingMessageDiv = messageDiv;
  const rawContent = messageDiv.dataset.rawContent || '';

  // 모달 생성 (없으면)
  let modal = document.getElementById('editMessageModal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'editMessageModal';
    modal.className = 'edit-message-modal';
    modal.innerHTML = `
      <div class="edit-message-container">
        <div class="edit-message-header">
          <button class="edit-close-btn" data-action="close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <button class="edit-save-btn" data-action="save">저장</button>
        </div>
        <textarea id="editMessageTextarea" class="edit-message-textarea" placeholder="내용을 입력하세요..."></textarea>
      </div>
    `;
    document.body.appendChild(modal);

    // 이벤트 위임
    modal.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (action === 'close') closeEditMessageModal();
      else if (action === 'save') saveEditedMessage();
    });
  }

  // 텍스트 영역에 내용 설정
  const textarea = document.getElementById('editMessageTextarea');
  textarea.value = rawContent;

  // 모달 표시
  modal.style.display = 'block';

  // 포커스 및 커서를 끝으로
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, 100);
}

export function closeEditMessageModal() {
  const modal = document.getElementById('editMessageModal');
  if (modal) modal.style.display = 'none';
  editingMessageDiv = null;
}

export async function saveEditedMessage() {
  if (!editingMessageDiv) {
    showToast('편집할 메시지가 없습니다.');
    return;
  }

  const newContent = document.getElementById('editMessageTextarea').value.trim();
  if (!newContent) {
    showToast('내용을 입력해주세요.');
    return;
  }

  const messageId = editingMessageDiv.dataset.messageId;
  if (!messageId) {
    showToast('메시지 ID를 찾을 수 없습니다.');
    return;
  }

  try {
    showGlobalLoading('저장 중...');

    const response = await authFetch(CHAT_ENDPOINTS.updateMessage, {
      body: {
        roomId: getCurrentRoomId(),
        sessionId: getCurrentSessionId(),
        messageId: messageId,
        newContent: newContent
      }
    });

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || '저장 실패');
    }

    // UI 업데이트
    editingMessageDiv.dataset.rawContent = newContent;
    const contentDiv = editingMessageDiv.querySelector('.message-content');
    if (contentDiv) {
      contentDiv.innerHTML = formatAIMessage(newContent);
    }

    hideGlobalLoading();
    closeEditMessageModal();
    showToast('저장되었습니다.');

  } catch (error) {
    hideGlobalLoading();
    console.error('Save edited message error:', error);
    showToast('저장 중 오류가 발생했습니다.', true);
  }
}

// ============================================
// 선택지 관련 함수
// ============================================

export function initChoiceToggle() {
  const toggleBtn = document.getElementById('choiceToggleBtn');
  if (toggleBtn) {
    if (isChoiceEnabled) {
      toggleBtn.classList.add('active');
    } else {
      toggleBtn.classList.remove('active');
    }
  }
}

export function displayChoices(choices) {
  currentChoices = choices.slice(0, 2);

  const container = document.getElementById('choiceContainer');
  const buttons = document.getElementById('choiceButtons');

  buttons.innerHTML = currentChoices.map(choice => `
    <button class="choice-btn" data-action="selectChoice">${escapeHtml(choice)}</button>
  `).join('');

  // 이벤트 위임 (기존 리스너 제거 후 추가)
  buttons.onclick = (e) => {
    const btn = e.target.closest('[data-action="selectChoice"]');
    if (btn) selectChoice(btn);
  };

  if (isChoiceEnabled) {
    container.style.display = 'block';
  }
}

export function toggleChoicePopup() {
  const container = document.getElementById('choiceContainer');
  const toggleBtn = document.getElementById('choiceToggleBtn');

  isChoiceEnabled = !isChoiceEnabled;
  localStorage.setItem('choiceEnabled', isChoiceEnabled);

  if (isChoiceEnabled) {
    toggleBtn.classList.add('active');
    if (currentChoices.length > 0) {
      container.style.display = 'block';
    }
    showToast('추천 답변 켜짐', 'info');
  } else {
    toggleBtn.classList.remove('active');
    container.style.display = 'none';
    showToast('추천 답변 꺼짐', 'info');
  }
}

export function hideChoicePopup() {
  const container = document.getElementById('choiceContainer');
  if (container) {
    container.style.display = 'none';
  }
}

export function selectChoice(btn) {
  const choiceText = btn.textContent;
  hideChoicePopup();
  sendMessage(choiceText);
}

export function clearChoices() {
  currentChoices = [];
  const container = document.getElementById('choiceContainer');
  if (container) {
    container.style.display = 'none';
  }
}

export function isChoiceFunctionEnabled() {
  return isChoiceEnabled;
}

// ============================================
// 입력 관련 함수
// ============================================

export function handleKeyDown(event) {
  // Enter는 줄바꿈 (기본 동작), 전송은 버튼으로만
}

export function insertActionMarkers() {
  const input = document.getElementById('messageInput');
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const text = input.value;

  if (start !== end) {
    const selectedText = text.substring(start, end);
    input.value = text.substring(0, start) + '*' + selectedText + '*' + text.substring(end);
    input.selectionStart = input.selectionEnd = end + 2;
  } else {
    input.value = text.substring(0, start) + '**' + text.substring(end);
    input.selectionStart = input.selectionEnd = start + 1;
  }

  input.focus();
}

// ============================================
// 상황 이미지 생성
// ============================================

export async function generateSceneImage(messageDiv) {
  if (isProcessing()) {
    showToast('응답 생성 중입니다. 잠시 후 시도해주세요.');
    return;
  }

  // 확인 모달
  const confirmed = await showConfirm(
    `상황에 맞는 이미지를 생성해요.\n진행 할까요? (10 ${CURRENCY.UNIT} 소모)`,
    '이미지 생성'
  );

  if (!confirmed) return;

  // 메시지 내용 추출
  const rawContent = messageDiv.dataset.rawContent ||
                     messageDiv.querySelector('.message-content')?.textContent || '';

  if (!rawContent.trim()) {
    showToast('메시지 내용을 찾을 수 없습니다.');
    return;
  }

  // chatData에서 정보 추출
  const chatData = window.currentChatData;
  if (!chatData) {
    showToast('채팅 데이터를 찾을 수 없습니다.');
    return;
  }

  const location = chatData.narrativeState?.location?.current || '';
  const time = chatData.narrativeState?.time?.current || '';

  // 캐릭터 이미지 URL 추출 (mode에 따라 다른 경로)
  let characterImageUrl = '';
  if (chatData.mode === 'story') {
    // 스토리 모드: 첫 번째 캐릭터 이미지 또는 images 배열
    characterImageUrl = chatData.characters?.[0]?.imageUrl || chatData.images?.characters?.[0] || '';
  } else {
    // 캐릭터 모드: character.imageUrl 또는 images 배열
    characterImageUrl = chatData.character?.imageUrl || chatData.images?.characters?.[0] || '';
  }

  if (!characterImageUrl) {
    showToast('점술가 이미지가 없습니다.');
    return;
  }

  try {
    showGlobalLoading();

    const response = await authFetch(CHAT_ENDPOINTS.generateSceneImage, {
      body: {
        roomId: getCurrentRoomId(),
        sessionId: getCurrentSessionId(),
        messageContent: rawContent,
        location,
        time,
        characterImageUrl
      }
    });

    if (response.status === 402) {
      hideGlobalLoading();
      showToast(`${CURRENCY.NAME}가 부족합니다.`);
      return;
    }

    if (response.status === 429) {
      hideGlobalLoading();
      const error = await response.json();
      showToast(error.error?.message || '요청이 너무 많습니다. 잠시 후 시도해주세요.');
      return;
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || '이미지 생성 실패');
    }

    hideGlobalLoading();

    // 채팅창에 이미지 메시지 추가
    const container = document.getElementById('messagesContainer');
    if (container && result.imageUrl) {
      const imageDiv = document.createElement('div');
      imageDiv.className = 'message assistant scene-image';
      imageDiv.innerHTML = `
        <div class="scene-image-container" data-action="showSceneImage" data-url="${result.imageUrl}">
          <img src="${result.imageUrl}" alt="생성된 이미지" loading="lazy">
          <div class="scene-image-overlay">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
            </svg>
          </div>
        </div>
      `;

      // 이미지 클릭 시 크게 보기
      imageDiv.querySelector('.scene-image-container').addEventListener('click', () => {
        showImageModal(result.imageUrl, '생성된 이미지');
      });

      container.appendChild(imageDiv);
      container.scrollTop = container.scrollHeight;
    }

    // 이미지 캐시 무효화
    invalidateImagesCache();
    showToast('이미지가 생성되었습니다.');

  } catch (error) {
    hideGlobalLoading();
    console.error('[generateSceneImage] 오류:', error);
    showToast(error.message || '이미지 생성 중 오류가 발생했습니다.');
  }
}

// ============================================
// window 노출 제거됨 (2026-01-06)
// 모든 호출이 ES6 import로 처리됨
// ============================================
